"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { WavesOverviewParams } from "@/types/waves.types";
import type { ApiWavesOverviewType } from "@/generated/models/ApiWavesOverviewType";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface UseWavesOverviewProps {
  readonly type: ApiWavesOverviewType;
  readonly limit?: number | undefined;
  readonly following?: boolean | undefined;
  readonly viewerIdentityKey?: string | null | undefined;
  /**
   * If true, fetch only direct message waves. If false, exclude them. Undefined -> no filter.
   */
  readonly directMessage?: boolean | undefined;
  readonly refetchInterval?: number | undefined;
}
export const useWavesOverview = t0 => {
  const $ = _c(49);
  const {
    type,
    limit: t1,
    following: t2,
    viewerIdentityKey,
    directMessage,
    refetchInterval: t3
  } = t0;
  const limit = t1 === undefined ? 20 : t1;
  const following = t2 === undefined ? false : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  let t4;
  if ($[0] !== directMessage) {
    t4 = directMessage !== undefined ? {
      direct_message: directMessage
    } : {};
    $[0] = directMessage;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== following || $[3] !== limit || $[4] !== t4 || $[5] !== type) {
    t5 = {
      limit,
      type,
      only_waves_followed_by_authenticated_user: following,
      ...t4
    };
    $[2] = following;
    $[3] = limit;
    $[4] = t4;
    $[5] = type;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const params = t5;
  let t6;
  if ($[7] !== viewerIdentityKey) {
    t6 = viewerIdentityKey?.trim().toLowerCase() ?? null;
    $[7] = viewerIdentityKey;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const normalizedViewerIdentityKey = t6;
  let t7;
  bb0: {
    if (!normalizedViewerIdentityKey) {
      t7 = params;
      break bb0;
    }
    let t8;
    if ($[9] !== normalizedViewerIdentityKey || $[10] !== params) {
      t8 = {
        ...params,
        viewer_identity: normalizedViewerIdentityKey
      };
      $[9] = normalizedViewerIdentityKey;
      $[10] = params;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    t7 = t8;
  }
  const queryKeyParams = t7;
  const [lastErrorTimestamp, setLastErrorTimestamp] = useState(null);
  let t8;
  if ($[12] !== queryKeyParams) {
    t8 = [QueryKey.WAVES_OVERVIEW, queryKeyParams];
    $[12] = queryKeyParams;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== params) {
    t9 = async t10 => {
      const {
        pageParam
      } = t10;
      const queryParams = {
        limit: `${params.limit}`,
        offset: `${pageParam}`,
        type: params.type,
        only_waves_followed_by_authenticated_user: `${params.only_waves_followed_by_authenticated_user}`
      };
      if (params.direct_message !== undefined) {
        queryParams.direct_message = `${params.direct_message}`;
      }
      return await commonApiFetch({
        endpoint: "waves-overview",
        params: queryParams
      });
    };
    $[14] = params;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== params) {
    t10 = (_, allPages) => allPages.at(-1)?.length === params.limit ? allPages.flat().length : null;
    $[16] = params;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== normalizedViewerIdentityKey) {
    t11 = (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1] as {
        viewer_identity?: string | null;
      } | undefined;
      const previousViewerIdentity = typeof previousParams?.viewer_identity === "string" ? previousParams.viewer_identity : null;
      if (previousViewerIdentity === normalizedViewerIdentityKey) {
        return previousData;
      }
    };
    $[18] = normalizedViewerIdentityKey;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = getDefaultQueryRetry(() => setLastErrorTimestamp(Date.now()));
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== refetchInterval || $[22] !== t10 || $[23] !== t11 || $[24] !== t8 || $[25] !== t9) {
    t13 = {
      queryKey: t8,
      queryFn: t9,
      initialPageParam: 0,
      getNextPageParam: t10,
      placeholderData: t11,
      refetchInterval,
      ...t12
    };
    $[21] = refetchInterval;
    $[22] = t10;
    $[23] = t11;
    $[24] = t8;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const query = useInfiniteQuery(t13);
  let t14;
  if ($[27] !== query.data?.pages) {
    t14 = () => query.data?.pages.flat() ?? [];
    $[27] = query.data?.pages;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const getWaves = t14;
  let t15;
  if ($[29] !== getWaves) {
    t15 = getWaves();
    $[29] = getWaves;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const [waves, setWaves] = useState(t15);
  let t16;
  if ($[31] !== getWaves) {
    t16 = () => {
      setWaves(getWaves());
    };
    $[31] = getWaves;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== query.data) {
    t17 = [query.data];
    $[33] = query.data;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  useEffect(t16, t17);
  let t18;
  if ($[35] !== lastErrorTimestamp || $[36] !== query) {
    t18 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.fetchNextPage();
        }, 30000);
        return;
      }
      query.fetchNextPage();
    };
    $[35] = lastErrorTimestamp;
    $[36] = query;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const fetchNextPage = t18;
  let t19;
  if ($[38] !== lastErrorTimestamp || $[39] !== query) {
    t19 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.refetch();
        }, 30000);
        return;
      }
      query.refetch();
    };
    $[38] = lastErrorTimestamp;
    $[39] = query;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const refetch = t19;
  let t20;
  if ($[41] !== fetchNextPage || $[42] !== query.hasNextPage || $[43] !== query.isFetching || $[44] !== query.isFetchingNextPage || $[45] !== query.status || $[46] !== refetch || $[47] !== waves) {
    t20 = {
      waves,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: query.hasNextPage,
      fetchNextPage,
      status: query.status,
      refetch
    };
    $[41] = fetchNextPage;
    $[42] = query.hasNextPage;
    $[43] = query.isFetching;
    $[44] = query.isFetchingNextPage;
    $[45] = query.status;
    $[46] = refetch;
    $[47] = waves;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const returnValue = t20;
  return returnValue;
};